import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/kibo-ui/combobox';
import { cn } from '@/lib/utils';
import { CheckIcon, ChevronsUpDownIcon, GaugeIcon } from 'lucide-react';
import { useState } from 'react';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';

type ImageResolutionSelectorProps = {
  id?: string;
  value: '1K' | '2K' | '3K' | '4K' | string;
  options?: Array<'1K' | '2K' | '3K' | '4K' | string>;
  width?: number | string | 'auto';
  className?: string;
  onChange?: (value: '1K' | '2K' | '3K' | '4K' | string) => void;
  disabled?: boolean;
};

const DEFAULT_RES_OPTIONS: Array<'1K' | '2K' | '3K' | '4K'> = ['1K', '2K', '3K', '4K'];

const getLabel = (option: string) => {
  return <div className="whitespace-nowrap">{option}</div>;
};

export const ImageResolutionSelector = ({
  id,
  value,
  width = 120,
  options,
  className,
  onChange,
  disabled,
}: ImageResolutionSelectorProps) => {
  const [open, setOpen] = useState(false);
  const isPreview = usePreviewCanvas();
  const effectiveDisabled = Boolean(disabled || isPreview);
  const opts = (options && options.length ? options : DEFAULT_RES_OPTIONS).map(String);

  return (
    <Combobox
      open={open}
      onOpenChange={(next) => {
        if (effectiveDisabled) return;
        setOpen(next);
      }}
      data={opts.map((option) => ({
        label: option,
        value: option,
      }))}
      type="resolution"
      value={String(value)}
      onValueChange={(val) => onChange?.(val)}
    >
      <ComboboxTrigger 
        id={id} 
        variant="ghost" 
        className={cn("rounded-xl h-9 px-3 hover:bg-sidebar-accent transition-all border-none font-medium", className)} 
        style={width === 'auto' ? {} : { width }} 
        disabled={effectiveDisabled}
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-2 overflow-hidden">
            <GaugeIcon size={14} className="shrink-0 text-muted-foreground opacity-70" />
            <span className="truncate">{getLabel(String(value || opts[0]))}</span>
          </div>
          <ChevronsUpDownIcon size={14} className="shrink-0 text-muted-foreground opacity-50" />
        </div>
      </ComboboxTrigger>
      <ComboboxContent
        className={cn('p-0', className)}
        popoverOptions={{ className: 'fm-toolbar-surface rounded-2xl border border-sidebar-border bg-sidebar/95 backdrop-blur-xl p-1.5 shadow-none' }}
      >
        <ComboboxList className="custom-scrollbar">
          <ComboboxEmpty />
          <ComboboxGroup className="p-1">
            {opts.map((option) => (
              <ComboboxItem
                key={option}
                value={option}
                onSelect={() => {
                  onChange?.(option);
                  setOpen(false);
                }}
                className="rounded-lg py-2 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer gap-2"
              >
                <GaugeIcon size={14} className="shrink-0 opacity-70" />
                {getLabel(option)}
                <CheckIcon
                  className={cn(
                    'ml-auto size-4 text-primary',
                    String(value) === option ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};


